// Builds a route tree from several nodes' reverse traces. Each node's trace runs
// node -> client, so reversing it gives client -> node; paths from the same
// client share their first hops and branch out toward each node, which merges
// into a tree rooted at the user ("You"). This is the physical-route tree view.

import type { RouteHop, RunResult, Tier } from './types';

export interface RouteTreeNode {
  /** Merge key: the hop host, or a unique token for a no-reply hop. */
  key: string;
  /** Hostname or IP; empty for a hop that did not reply. */
  host: string;
  /** Display label. */
  label: string;
  /** All hop samples that landed on this tree node. */
  hops: RouteHop[];
  /** Target node ids whose path passes through this hop. */
  nodeIds: string[];
  /** Tiers whose path passes through this hop. */
  tiers: Tier[];
  /** Worst per-hop loss seen here, for colouring. */
  worstLossPct: number;
  /** Representative average latency at this hop (ms). */
  avgMs: number;
  children: RouteTreeNode[];
}

function newNode(key: string, host: string): RouteTreeNode {
  return {
    key,
    host,
    label: host || '(no reply)',
    hops: [],
    nodeIds: [],
    tiers: [],
    worstLossPct: 0,
    avgMs: 0,
    children: [],
  };
}

/**
 * Merge the reverse traces of several runs into a single tree rooted at "You".
 * Runs without a route are ignored. Returns null when no run has a route.
 */
export function buildRouteTree(results: RunResult[]): RouteTreeNode | null {
  const withRoute = results.filter((r) => r.route && r.route.length > 0);
  if (withRoute.length === 0) return null;

  const root = newNode('__root', '');
  root.label = 'You';

  for (const r of withRoute) {
    const reversed = [...(r.route as RouteHop[])].reverse(); // client -> node
    let cur = root;
    reversed.forEach((hop, i) => {
      // Known hosts merge; a no-reply hop stays unique so distinct unknown hops
      // are never collapsed into one.
      const key = hop.host ? `h:${hop.host}` : `u:${r.nodeId}:${i}`;
      let child = cur.children.find((c) => c.key === key);
      if (!child) {
        child = newNode(key, hop.host ?? '');
        child.avgMs = hop.avgMs;
        cur.children.push(child);
      }
      child.hops.push(hop);
      child.worstLossPct = Math.max(child.worstLossPct, hop.lossPct);
      if (!child.nodeIds.includes(r.nodeId)) child.nodeIds.push(r.nodeId);
      if (r.tier && !child.tiers.includes(r.tier)) child.tiers.push(r.tier);
      cur = child;
    });
  }
  return root;
}
